import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Mail, RefreshCw, ArrowLeft, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';

const VerifyPendingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const email = location.state?.email || '';
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const handleResend = async () => {
    if (!email) {
      toast.error('Email tidak ditemukan. Silakan daftar ulang.');
      navigate('/register');
      return;
    }

    setResending(true);
    try {
      await axios.post(`${API}/auth/resend-verification?email=${encodeURIComponent(email)}`);
      toast.success('Link verifikasi baru telah dikirim!');
      setCooldown(60);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal mengirim ulang. Coba lagi nanti.');
    } finally {
      setResending(false);
    }
  };

  React.useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0A0A0A] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white dark:bg-zinc-900 rounded-xl shadow-xl p-8 text-center">
          {/* Icon */}
          <div className="w-20 h-20 mx-auto mb-6 bg-indigo-100 dark:bg-indigo-950/40 rounded-full flex items-center justify-center">
            <Mail className="w-10 h-10 text-indigo-600 dark:text-indigo-400" />
          </div>

          {/* Title */}
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50 mb-2" style={{ fontFamily: 'Manrope' }}>
            Cek Email Anda
          </h1>

          {/* Description */}
          <p className="text-slate-600 dark:text-zinc-400 mb-6">
            Kami telah mengirim link verifikasi ke:
          </p>

          {/* Email Display */}
          <div className="bg-slate-100 dark:bg-zinc-800 rounded-lg px-4 py-3 mb-6 overflow-hidden">
            <p className="font-medium text-slate-900 dark:text-zinc-50 text-sm break-all">
              {email || 'email@example.com'}
            </p>
          </div>

          {/* Instructions */}
          <div className="text-left bg-indigo-50 dark:bg-indigo-950/30 rounded-lg p-4 mb-6">
            <h3 className="font-medium text-blue-800 dark:text-indigo-300 mb-2 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              Langkah selanjutnya:
            </h3>
            <ol className="text-sm text-indigo-700 dark:text-indigo-400 space-y-1 list-decimal list-inside">
              <li>Buka inbox email Anda</li>
              <li>Cari email dari Nerava</li>
              <li>Klik tombol "Verifikasi Email Saya"</li>
              <li>Setelah terverifikasi, Anda bisa login</li>
            </ol>
          </div>

          {/* Resend Button */}
          <div className="space-y-3">
            <p className="text-sm text-slate-500 dark:text-zinc-400">
              Tidak menerima email?
            </p>
            <Button
              variant="outline"
              onClick={handleResend}
              disabled={resending || cooldown > 0}
              className="w-full"
              data-testid="resend-verification-btn"
            >
              {resending ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Mengirim...
                </>
              ) : cooldown > 0 ? (
                <>
                  <CheckCircle className="w-4 h-4 mr-2 text-indigo-600 dark:text-indigo-400" />
                  Terkirim! Kirim ulang ({cooldown}s)
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Kirim Ulang Link Verifikasi
                </>
              )}
            </Button>
          </div>

          {/* Back to Login */}
          <div className="mt-8 pt-6 border-t border-slate-200 dark:border-zinc-800">
            <Link 
              to="/login" 
              className="inline-flex items-center text-sm text-slate-600 dark:text-zinc-400 hover:text-indigo-600 dark:hover:text-indigo-400 dark:hover:text-blue-400"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Kembali ke halaman Login
            </Link>
          </div>
        </div>

        {/* Footer Note */}
        <p className="text-center text-xs text-slate-500 dark:text-zinc-400 mt-6">
          Link verifikasi akan kadaluarsa dalam 24 jam.
          <br />
          Cek juga folder Spam atau Promosi.
        </p>
      </div>
    </div>
  );
};

export default VerifyPendingPage;
